import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgForage } from 'ngforage';
import * as allSettled from 'promise.allsettled';
import AssetHelper from 'src/app/helpers/asset-helper';
import ErrorHelper from 'src/app/helpers/error-helper';
import Asset from 'src/app/model/asset';
import ProductAsset from 'src/app/model/product-asset';
import { ImageCompressionService } from '../../image-compression.service';
import { LoadingService } from '../../loading.service';
import { OfflineAsset } from '../../placer-offline-changes/offline-asset';
import { SnackService } from '../../snack.service';
import { Observable } from 'rxjs';
import { _post } from '../../base.service';

@Injectable({
  providedIn: 'root',
})
export class TourInstallerSubmitAssetControllerRequestService {
  private readonly compressIfLargerThan = 524288; // 0.5 MB
  private static BASE_URL = '/tourInstaller';
  private readonly post: <T>(
    path: string,
    body: RequestBody | NotExecutedHistoryItemAssetRequestBody,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly deserialize = (res) => res as ProductAsset;
  private readonly deserializeNotExecutedHistoryItemAssetResponse = (res) =>
    res as NotExecutedHistoryItemAssetResponse;
  constructor(
    http: HttpClient,
    private snackService: SnackService,
    loadingService: LoadingService,
    router: Router,
    private ngForage: NgForage,
    private imageCompressionService: ImageCompressionService
  ) {
    const requestServices = {
      http,
      loadingService,
      router,
      snackService,
    };
    this.post = _post(
      TourInstallerSubmitAssetControllerRequestService.BASE_URL,
      requestServices
    );
  }

  public async submitAsset(
    offlineAsset: OfflineAsset,
    productId: number,
    subProjectId: number,
    immediateSubmit: boolean,
    showErrorMessage = false
  ): Promise<ProductAsset> {
    const file = await this.ngForage.getItem<File>(
      AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
    );
    if (file == null) {
      throw new Error(`File no longer exists: ${offlineAsset.localFileUuid}`);
    }
    const compressedFile =
      file.size > this.compressIfLargerThan
        ? await this.imageCompressionService.compressImage(file)
        : file;
    const base64 = await AssetHelper.extractBase64(compressedFile);
    const requestBody: RequestBody = {
      name: compressedFile.name,
      type: compressedFile.type,
      size: compressedFile.size,
      formSection: offlineAsset.formSection,
      base64: base64,
      productId: productId,
      subProjectId: subProjectId,
      localFileUuid: offlineAsset.localFileUuid,
    };
    try {
      return await this.post(
        '/submitAsset',
        requestBody,
        this.deserialize
      ).toPromise();
    } catch (err) {
      if (
        ErrorHelper.isConnectionLostError(err) &&
        immediateSubmit &&
        showErrorMessage
      ) {
        this.snackService.showWarning(
          'Server kon niet bereikt worden, opdracht zal ingediend worden zodra internetverbinding hersteld is'
        );
      } else if (immediateSubmit && showErrorMessage) {
        this.snackService.showError(err);
      }
      throw err;
    }
  }

  public async submitMultipleOfflineAssets(
    offlineAssets: OfflineAsset[],
    productId: number | null,
    subProjectId: number | null,
    immediateSubmit: boolean
  ): Promise<MultipleAssetSubmitResult> {
    const results = await allSettled(
      offlineAssets.map((a) =>
        this.submitAsset(a, productId, subProjectId, immediateSubmit)
      )
    );
    const resultMap = new Map<string, ProductAsset>();
    let error = null;
    for (const result of results) {
      if (result.status === 'fulfilled') {
        resultMap.set(result.value.localFileUuid, result.value);
      } else if (result.status === 'rejected') {
        error =
          error == null
            ? result.reason
            : ErrorHelper.isConnectionLostError(error)
            ? error
            : result.reason;
      }
    }
    return {
      resultMap: resultMap,
      error: error,
    };
  }

  public async submitNotExecutedHistoryItemAsset(
    offlineAsset: OfflineAsset,
    notExecutedHistoryItemId: number,
    immediateSubmit: boolean,
    showErrorMessage = false
  ): Promise<NotExecutedHistoryItemAssetResponse> {
    const file = await this.ngForage.getItem<File>(
      AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
    );
    if (file == null) {
      throw new Error(`File no longer exists: ${offlineAsset.localFileUuid}`);
    }
    const compressedFile =
      file.size > this.compressIfLargerThan
        ? await this.imageCompressionService.compressImage(file)
        : file;
    const base64 = await AssetHelper.extractBase64(compressedFile);
    const requestBody: NotExecutedHistoryItemAssetRequestBody = {
      name: compressedFile.name,
      type: compressedFile.type,
      size: compressedFile.size,
      base64: base64,
      notExecutedHistoryItemId: notExecutedHistoryItemId,
      localFileUuid: offlineAsset.localFileUuid,
    };
    try {
      return await this.post(
        '/submitNotExecutedHistoryItemAsset',
        requestBody,
        this.deserializeNotExecutedHistoryItemAssetResponse
      ).toPromise();
    } catch (err) {
      if (
        ErrorHelper.isConnectionLostError(err) &&
        immediateSubmit &&
        showErrorMessage
      ) {
        this.snackService.showWarning(
          'Server kon niet bereikt worden, foto zal ingediend worden zodra internetverbinding hersteld is'
        );
      } else if (immediateSubmit && showErrorMessage) {
        this.snackService.showError(err);
      }
      throw err;
    }
  }

  public async submitMultipleNotExecutedHistoryItemAssets(
    offlineAssets: OfflineAsset[],
    notExecutedHistoryItemId: number,
    immediateSubmit: boolean
  ): Promise<MultipleNotExecutedHistoryItemAssetSubmitResult> {
    const results = await allSettled(
      offlineAssets.map((a) =>
        this.submitNotExecutedHistoryItemAsset(
          a,
          notExecutedHistoryItemId,
          immediateSubmit
        )
      )
    );
    const resultMap = new Map<string, Asset>();
    let error = null;
    for (const result of results) {
      if (result.status === 'fulfilled') {
        resultMap.set(result.value.localFileUuid, result.value.asset);
      } else if (result.status === 'rejected') {
        error =
          error == null
            ? result.reason
            : ErrorHelper.isConnectionLostError(error)
            ? error
            : result.reason;
      }
    }
    return {
      resultMap: resultMap,
      error: error,
    };
  }
}
interface RequestBody {
  name: string;
  type: string;
  size: number;
  formSection: string;
  base64: string;
  localFileUuid: string;
  productId?: number;
  subProjectId?: number;
}

interface NotExecutedHistoryItemAssetRequestBody {
  name: string;
  type: string;
  size: number;
  base64: string;
  localFileUuid: string;
  notExecutedHistoryItemId: number;
}

interface NotExecutedHistoryItemAssetResponse {
  id: number;
  asset: Asset;
  localFileUuid: string;
}

export interface MultipleAssetSubmitResult {
  resultMap: Map<string, ProductAsset>;
  error?: any;
}

export interface MultipleNotExecutedHistoryItemAssetSubmitResult {
  resultMap: Map<string, Asset>;
  error?: any;
}
